import { WEBHOOK_EVENT_TYPES } from '@monorepo/shared/schemas/common'
import type {
  CreateWebhook,
  WebhookFilters,
} from '@monorepo/shared/schemas/webhooks'
import { Globe, Loader2, Plus } from 'lucide-react'
import { useState } from 'react'
import { Button } from '@/shared/components/ui/button'
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from '@/shared/components/ui/drawer'
import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'
import { extractErrorMessage } from '@/shared/lib/error-utils'
import { toast } from '@/shared/lib/toast'
import { webhookService } from '../services/webhookService'
import { AgentActionFilter } from './AgentActionFilter'
import { EventSelector } from './EventSelector'

interface AddWebhookDrawerProps {
  onWebhookCreated?: () => void
}

// Group event types by prefix (e.g., "agent.", "invoice.", etc.)
function groupEventsByCategory(
  events: readonly string[]
): Record<string, string[]> {
  const groups: Record<string, string[]> = {}
  for (const event of events) {
    const prefix = event.split('.')[0]
    if (!groups[prefix]) groups[prefix] = []
    groups[prefix].push(event)
  }
  return groups
}

export function AddWebhookDrawer({ onWebhookCreated }: AddWebhookDrawerProps) {
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)

  // Form state
  const [description, setDescription] = useState('')
  const [endpointUrl, setEndpointUrl] = useState('')
  const [selectedEvents, setSelectedEvents] = useState<string[]>([])
  const [webhookFilters, setWebhookFilters] = useState<WebhookFilters>({})

  const eventCategories = groupEventsByCategory(WEBHOOK_EVENT_TYPES)

  const resetForm = () => {
    setDescription('')
    setEndpointUrl('')
    setSelectedEvents([])
    setWebhookFilters({})
  }

  const handleSubmit = async () => {
    if (!endpointUrl || selectedEvents.length === 0) {
      return
    }

    setLoading(true)
    try {
      const hasAgentFilters =
        webhookFilters.agents && webhookFilters.agents.length > 0
      const webhookData: CreateWebhook = {
        url: endpointUrl,
        events: selectedEvents as CreateWebhook['events'],
        description: description || undefined,
        active: true,
        ...(hasAgentFilters ? { filters: webhookFilters } : {}),
      }

      await webhookService.createWebhook(webhookData)
      toast.success('Webhook created successfully')

      resetForm()
      setOpen(false)
      onWebhookCreated?.()
    } catch (error) {
      toast.error(extractErrorMessage(error))
    } finally {
      setLoading(false)
    }
  }

  const isValidUrl = (url: string) => {
    try {
      const urlObj = new URL(url)
      return urlObj.protocol === 'http:' || urlObj.protocol === 'https:'
    } catch {
      return false
    }
  }

  const isFormValid =
    endpointUrl && isValidUrl(endpointUrl) && selectedEvents.length > 0

  return (
    <Drawer
      open={open}
      onOpenChange={newOpen => {
        setOpen(newOpen)
        if (!newOpen) {
          resetForm()
        }
      }}
      direction='right'
    >
      <DrawerTrigger asChild>
        <Button className='gap-2'>
          <Plus className='w-4 h-4' />
          Add webhook
        </Button>
      </DrawerTrigger>
      <DrawerContent className='h-screen w-[50vw] min-w-[600px] max-w-[800px] ml-auto fixed inset-y-0 right-0 flex flex-col'>
        <DrawerHeader className='border-b flex-shrink-0 px-6 py-4'>
          <DrawerTitle>Add New Webhook</DrawerTitle>
          <DrawerDescription>
            Create a new webhook to receive real-time notifications about events
            in your project.
          </DrawerDescription>
        </DrawerHeader>
        <div className='flex-1 overflow-y-auto px-6 py-4'>
          <div className='space-y-6'>
            {/* Description */}
            <div className='space-y-2'>
              <Label htmlFor='description'>Description</Label>
              <Input
                id='description'
                value={description}
                onChange={e => setDescription(e.target.value)}
                placeholder='e.g., analytics-service'
              />
            </div>

            {/* Endpoint URL */}
            <div className='space-y-2'>
              <Label htmlFor='endpoint-url'>Endpoint URL *</Label>
              <div className='relative'>
                <Globe className='absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground' />
                <Input
                  id='endpoint-url'
                  type='url'
                  value={endpointUrl}
                  onChange={e => setEndpointUrl(e.target.value)}
                  placeholder='https://your-service.com/webhooks'
                  className='pl-10'
                />
              </div>
              {endpointUrl && !isValidUrl(endpointUrl) && (
                <p className='text-sm text-red-600'>
                  Please enter a valid URL starting with http:// or https://
                </p>
              )}
            </div>

            {/* Event Selection */}
            <EventSelector
              eventCategories={eventCategories}
              selectedEvents={selectedEvents}
              onSelectionChange={setSelectedEvents}
            />

            {/* Agent/Action Filter - shown when agent events are selected */}
            {selectedEvents.some(e => e.startsWith('agent.')) && (
              <AgentActionFilter
                filters={webhookFilters}
                onFiltersChange={setWebhookFilters}
              />
            )}
          </div>
        </div>

        {/* Fixed Footer with Create Button */}
        <div className='border-t bg-background px-6 py-4 flex-shrink-0'>
          <div className='flex justify-end'>
            <Button
              onClick={handleSubmit}
              disabled={!isFormValid || loading}
              className='gap-2'
            >
              {loading ? (
                <>
                  <Loader2 className='w-4 h-4 animate-spin' />
                  Creating...
                </>
              ) : (
                <>
                  <Plus className='w-4 h-4' />
                  Create Webhook
                </>
              )}
            </Button>
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  )
}
